import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Alert,
  Paper,
  Chip,
  Grid,
} from '@mui/material';
import { AutoAwesome, Business, Security, Factory, Restaurant } from '@mui/icons-material';
import ContextAssistant from './ContextAssistant';

interface TemplateInputs {
  topic: string;
  industry: string;
  target_question_count: number;
  target_category_count: number;
  additional_context: string;
}

interface TemplateInputFormProps {
  onSubmit: (inputs: TemplateInputs) => void;
  isLoading: boolean;
  error: string | null;
  initialData?: TemplateInputs | null;
}

/** Suggest category count from question total (~10 questions each, clamped 3-12). */
const suggestCategoryCount = (questionCount: number): number =>
  Math.max(3, Math.min(12, Math.floor(questionCount / 10)));

const TemplateInputForm: React.FC<TemplateInputFormProps> = ({
  onSubmit,
  isLoading,
  error,
  initialData,
}) => {
  const defaultQuestionCount = 20;
  const [formData, setFormData] = useState<TemplateInputs>(
    initialData || {
      topic: '',
      industry: '',
      target_question_count: defaultQuestionCount,
      target_category_count: suggestCategoryCount(defaultQuestionCount),
      additional_context: '',
    }
  );

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  // When false, changing question count keeps the category guide in sync
  const [categoryManuallySet, setCategoryManuallySet] = useState(false);
  
  // Shared state for context assistant (split button and panel)
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isFormattingContext, setIsFormattingContext] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<any>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<any>({});
  const [contextError, setContextError] = useState<string | null>(null);
  
  const sharedContextState = {
    isLoadingSuggestions,
    setIsLoadingSuggestions,
    isFormattingContext,
    setIsFormattingContext,
    suggestionsOpen,
    setSuggestionsOpen,
    suggestions,
    setSuggestions,
    selectedAnswers,
    setSelectedAnswers,
    error: contextError,
    setError: setContextError,
  };

  // Common industry options
  const industryOptions = [
    { value: '', label: 'Any Industry', icon: <Business /> },
    { value: 'Manufacturing', label: 'Manufacturing', icon: <Factory /> },
    { value: 'Software Development', label: 'Software Development', icon: <Business /> },
    { value: 'Healthcare', label: 'Healthcare', icon: <Business /> },
    { value: 'Finance', label: 'Financial Services', icon: <Business /> },
    { value: 'Retail', label: 'Retail', icon: <Business /> },
    { value: 'Food & Beverage', label: 'Food & Beverage', icon: <Restaurant /> },
    { value: 'Construction', label: 'Construction', icon: <Business /> },
    { value: 'Education', label: 'Education', icon: <Business /> },
    { value: 'Transportation', label: 'Transportation & Logistics', icon: <Business /> },
  ];

  // Popular topic suggestions
  const topicSuggestions = [
    'IT Security Compliance',
    'Supply Chain Management', 
    'Quality Management System',
    'Health & Safety Compliance',
    'Financial Controls',
    'Data Protection & Privacy',
    'Environmental Management',
    'Customer Service Standards',
    'Inventory Management',
    'Business Continuity Planning',
  ];

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.topic.trim()) {
      newErrors.topic = 'Topic is required';
    } else if (formData.topic.length < 3) {
      newErrors.topic = 'Topic must be at least 3 characters';
    }

    if (formData.target_question_count < 5) {
      newErrors.target_question_count = 'Minimum 5 questions required';
    } else if (formData.target_question_count > 200) {
      newErrors.target_question_count = 'Maximum 200 questions allowed';
    }

    if (formData.target_category_count < 3) {
      newErrors.target_category_count = 'Minimum 3 categories required';
    } else if (formData.target_category_count > 12) {
      newErrors.target_category_count = 'Maximum 12 categories allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleTopicSuggestionClick = (suggestion: string) => {
    setFormData({ ...formData, topic: suggestion });
    setErrors({ ...errors, topic: '' });
  };

  const handleQuestionSliderChange = (_event: Event, newValue: number | number[]) => {
    const questionCount = newValue as number;
    setFormData({
      ...formData,
      target_question_count: questionCount,
      // Keep category guide aligned until the user chooses their own count
      ...(categoryManuallySet
        ? {}
        : { target_category_count: suggestCategoryCount(questionCount) }),
    });
    setErrors({ ...errors, target_question_count: '' });
  };

  const handleCategorySliderChange = (_event: Event, newValue: number | number[]) => {
    setCategoryManuallySet(true);
    setFormData({
      ...formData,
      target_category_count: newValue as number,
    });
    setErrors({ ...errors, target_category_count: '' });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesome color="primary" />
        Tell AI About Your Audit Template
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Provide some details about the audit template you want to create. The AI will generate 
        a structured template with relevant questions and answer options.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Topic Input */}
        <TextField
          label="Review Topic *"
          fullWidth
          value={formData.topic}
          onChange={(e) => {
            setFormData({ ...formData, topic: e.target.value });
            setErrors({ ...errors, topic: '' });
          }}
          error={!!errors.topic}
          helperText={errors.topic || 'What area should this audit focus on? (e.g., "IT Security", "Supply Chain Management")'}
          placeholder="Enter the main topic or focus area for your audit"
          disabled={isLoading}
          inputProps={{ 'data-testid': 'template-topic-input' }}
        />

        {/* Topic Suggestions */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            💡 Popular Topics:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {topicSuggestions.map((suggestion) => (
              <Chip
                key={suggestion}
                label={suggestion}
                variant="outlined"
                size="small"
                onClick={() => handleTopicSuggestionClick(suggestion)}
                sx={{ cursor: 'pointer' }}
                disabled={isLoading}
              />
            ))}
          </Box>
        </Box>

        {/* Industry */}
        <FormControl fullWidth>
          <InputLabel>Industry (Optional)</InputLabel>
          <Select
            value={formData.industry}
            label="Industry (Optional)"
            onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
            disabled={isLoading}
            inputProps={{ 'data-testid': 'template-industry-select' }}
          >
            {industryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.icon}
                  {option.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Question and Category Count Row */}
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <Typography gutterBottom>
              Up to {formData.target_question_count} questions (ceiling)
            </Typography>
            <Slider
              value={formData.target_question_count}
              onChange={handleQuestionSliderChange}
              aria-labelledby="question-count-slider"
              valueLabelDisplay="auto"
              step={5}
              min={5}
              max={100}
              marks={[
                { value: 5, label: '5' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
              data-testid="template-question-count-slider"
              disabled={isLoading}
              color={errors.target_question_count ? 'error' : 'primary'}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              AI will generate at most this many distinct questions — it will not pad with duplicates.
            </Typography>
            {errors.target_question_count && (
              <Typography variant="caption" color="error">
                {errors.target_question_count}
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography gutterBottom>
              Target Number of Categories: {formData.target_category_count}
            </Typography>
            <Slider
              value={formData.target_category_count}
              onChange={handleCategorySliderChange}
              aria-labelledby="category-count-slider"
              valueLabelDisplay="auto"
              step={1}
              min={3}
              max={12}
              marks={[
                { value: 3, label: '3' },
                { value: 6, label: '6' },
                { value: 9, label: '9' },
                { value: 12, label: '12' },
              ]}
              data-testid="template-category-count-slider"
              disabled={isLoading}
              color={errors.target_category_count ? 'error' : 'primary'}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Guide only — AI may put more questions in important areas rather than splitting evenly.
              Around 6–7 categories usually works well for radar charts.
            </Typography>
            {errors.target_category_count && (
              <Typography variant="caption" color="error" display="block">
                {errors.target_category_count}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Additional Context */}
        <Box>
          {/* AI Context Assistant - Get Suggestions Button (above) */}
          <ContextAssistant
            topic={formData.topic}
            industry={formData.industry}
            currentContext={formData.additional_context}
            onContextUpdate={(newContext) => setFormData({ ...formData, additional_context: newContext })}
            disabled={isLoading}
            showButton={true}
            showPanel={false}
            sharedState={sharedContextState}
          />
          
          <TextField
            label="Additional Context (Optional)"
            fullWidth
            multiline
            minRows={3}
            maxRows={15}
            value={formData.additional_context}
            onChange={(e) => setFormData({ ...formData, additional_context: e.target.value })}
            placeholder="Any specific requirements, focus areas, or constraints for this review template?"
            helperText={
              formData.additional_context.trim().length > 0 &&
              formData.additional_context.trim().length < 40
                ? 'Brief looks thin — use Context Assistant to add how work is done, what good looks like, and scope.'
                : 'Add how work is done today, what good looks like, scope, and any critical risks. Thin briefs produce thinner templates.'
            }
            error={
              formData.additional_context.trim().length > 0 &&
              formData.additional_context.trim().length < 40
            }
            disabled={isLoading}
            sx={{ 
              '& textarea': {
                resize: 'vertical !important'
              }
            }}
          />
          
          {/* AI Context Assistant - Suggestions Panel (below) */}
          <Box sx={{ mt: 1 }}>
            <ContextAssistant
              topic={formData.topic}
              industry={formData.industry}
              currentContext={formData.additional_context}
              onContextUpdate={(newContext) => setFormData({ ...formData, additional_context: newContext })}
              disabled={isLoading}
              showButton={false}
              showPanel={true}
              sharedState={sharedContextState}
            />
          </Box>
        </Box>

        {/* Submit Button */}
        <Paper sx={{ 
          p: 3, 
          bgcolor: 'primary.light',
          color: 'primary.contrastText',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Ready to Generate Your Template?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                AI will create up to {formData.target_question_count} distinct questions
                across about {formData.target_category_count} categories
                {formData.industry && ` tailored for ${formData.industry}`} businesses.
                Fewer is OK when quality requires it.
              </Typography>
            </Box>
            <Button
              type="submit"
              variant="outlined"
              size="small"
              disabled={isLoading || !formData.topic.trim()}
              startIcon={isLoading ? <CircularProgress size={20} /> : <AutoAwesome />}
              sx={{ 
                background: '#FFFFFF',
                color: '#1a1a1a',
                fontWeight: 700,
                borderColor: 'rgba(0, 0, 0, 0.2)',
                borderWidth: '2px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                px: 3,
                py: 1.5,
                '&:hover': {
                  background: '#F5F5F5',
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                  borderWidth: '2px',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.3)',
                },
                '&.Mui-disabled': {
                  background: 'rgba(255, 255, 255, 0.4)',
                  color: 'rgba(0, 0, 0, 0.3)',
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                }
              }}
              data-testid="generate-template-button"
            >
              {isLoading ? 'Generating...' : 'Generate Template'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default TemplateInputForm; 